import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../api';
import PresetTemplates from './PresetTemplates';
import AcademicSystemConfig from './AcademicSystemConfig';
import RulesSummary from './RulesSummary';
import Toast from './Toast';
import AttendancePolicyConfig from './AttendancePolicyConfig';

// Icons as SVG constants for a cleaner render block
const Icons = {
  Grading: () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Attendance: () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Back: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  ),
  Template: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  ),
  Check: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
    </svg>
  ),
  Warning: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  )
};

const CARD_GRADING = 'grading';
const CARD_ATTENDANCE = 'attendance';
const CARD_TEMPLATES = 'templates';

const SubjectAttendanceRules = ({ userId, token, tenantId }) => {
  const location = useLocation();
  const navState = location.state || {};
  
  const effectiveTenantId = tenantId || navState.tenantId || localStorage.getItem('tenantId') || null;
  const effectiveUserId = userId || navState.userId || localStorage.getItem('userId') || null;
  const effectiveToken = token || navState.token || localStorage.getItem('token') || null;

  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [, setAttendanceBlockedWarning] = useState(false);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => removeToast(id), 5000);
  }, [removeToast]);

  const fetchRules = useCallback(async () => {
    if (!effectiveTenantId) {
      setRules(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const headers = effectiveToken ? { Authorization: `Bearer ${effectiveToken}` } : {};
      const response = await api.get(`/api/grading-attendance-rules/${effectiveTenantId}`, { headers });
      
      if (response.data && response.data.success) {
        setRules(response.data.rules);
        // Check if grading rules are configured (has gradingType)
        const hasGradingRules = response.data.rules && response.data.rules.gradingType;
        if (!hasGradingRules) {
          setShowPresets(true);
        }
      } else {
        setRules(null);
        setShowPresets(true);
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        setRules(null);
        setShowPresets(true);
      } else {
        addToast('Error loading rules: ' + (error.response?.data?.message || error.message), 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, effectiveToken, addToast]);

  const updateRules = async (updatedRules) => {
    try {
      setSaving(true);
      if (!effectiveUserId) throw new Error('User ID is required for saving rules');
      const requestData = { ...updatedRules, userId: effectiveUserId };
      const response = await api.put(`/api/grading-attendance-rules/${effectiveTenantId}`, requestData);
      if (response.data.success) {
        setRules(response.data.rules);
        addToast('Grading rules saved successfully', 'success');
        return true;
      }
      return false;
    } catch (error) {
      addToast('Error saving rules: ' + (error.response?.data?.message || error.message), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateAttendancePolicy = async (policy) => {
    try {
      setSaving(true);
      if (!effectiveUserId) throw new Error('User ID is required for saving policy');
      
      // Check if grading rules are configured before saving attendance
      if (!rules || !rules.gradingType) {
        setAttendanceBlockedWarning(true);
        setTimeout(() => setAttendanceBlockedWarning(false), 5000);
        addToast('Please configure grading rules first before setting attendance policy', 'warning');
        return false;
      }
      
      const headers = effectiveToken ? { Authorization: `Bearer ${effectiveToken}` } : {};
      const response = await api.put(`/api/grading-attendance-rules/${effectiveTenantId}/attendance-policy`, {
        attendancePolicy: policy,
        userId: effectiveUserId
      }, { headers });
      
      if (response.data.success) {
        setRules(prev => ({ ...prev, attendancePolicy: response.data.attendancePolicy }));
        addToast('Attendance policy updated successfully', 'success');
        return true;
      }
      return false;
    } catch (error) {
      addToast('Error updating attendance policy: ' + (error.response?.data?.message || error.message), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = async (templateKey) => {
    try {
      setSaving(true);
      if (!effectiveUserId) throw new Error('User ID is required for applying templates');
      const requestData = { templateKey, userId: effectiveUserId };
      const response = await api.post(`/api/grading-attendance-rules/${effectiveTenantId}/apply-template`, requestData);
      if (response.data.success) {
        setRules(response.data.rules);
        setShowPresets(false);
        setActiveCard(CARD_GRADING);
        addToast(`Applied ${templateKey.replace('_', ' ')} template successfully`, 'success');
        return true;
      }
      return false;
    } catch (error) {
      addToast('Error applying template: ' + (error.response?.data?.message || error.message), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Check if grading rules are configured
  const hasGradingRules = rules && rules.gradingType;
  const hasAttendancePolicy = rules && rules.attendancePolicy;

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  if (!effectiveTenantId && !loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
        <div className="bg-red-50 p-6 rounded-2xl border border-red-100 max-w-md">
          <div className="text-red-500 mb-4 text-4xl font-bold">!</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tenant Selected</h3>
          <p className="text-gray-600">Please access this panel through the Admin Dashboard or ensure a valid Tenant ID is provided.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
          </div>
        </div>
        <p className="mt-6 text-gray-500 font-medium animate-pulse">Synchronizing academic rules...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 pb-20">
      {/* Toast System */}
      <div className="fixed top-6 right-6 z-[60] flex flex-col gap-3">
        {toasts.map((toast) => (
          <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 pt-10">
        {/* Modern Header */}
        <header className="mb-12 text-center">
          <span className="px-3 py-1 text-xs font-bold tracking-widest text-blue-600 uppercase bg-blue-50 rounded-full">
            Configuration Hub
          </span>
          <h1 className="mt-4 text-4xl font-extrabold text-gray-900 tracking-tight">
            Institutional Policies
          </h1>
          <p className="mt-3 text-lg text-gray-500 max-w-2xl mx-auto">
            Define the framework for your academic excellence—from grading structures to attendance management.
          </p>
          
          {/* Configuration Status Badge */}
          {hasGradingRules && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-green-700">
                Grading Rules: {rules.gradingType?.toUpperCase()} | 
                Pass: {rules.overallPassingPercentage}% |
                {rules.requireBothToPass ? ' Must pass both' : ' Aggregate only'}
              </span>
            </div>
          )}
        </header>

        {/* Selection Grid */}
        {!showPresets && !activeCard && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Grading Rules Card */}
            <button
              onClick={() => setActiveCard(CARD_GRADING)}
              className="group relative bg-white p-8 rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-300 text-left overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Icons.Grading />
              </div>
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Icons.Grading />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Grading & Assessment</h3>
              <p className="text-gray-500 mb-6 leading-relaxed">
                Configure grading type (Percentage/Grade/CGPA), passing criteria, and grade thresholds.
              </p>
              <div className="flex flex-wrap gap-2">
                {hasGradingRules ? (
                  <>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {rules.gradingType === 'percentage' ? 'Percentage' : 
                       rules.gradingType === 'grade' ? 'Letter Grades' : 'CGPA'}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700 px-2 py-1 rounded">
                      ✓ Configured
                    </span>
                  </>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                    ⚠️ Not Configured
                  </span>
                )}
              </div>
            </button>

            {/* Attendance Card - Shows warning if grading not configured */}
            <button
              onClick={() => {
                if (!hasGradingRules) {
                  addToast('Please configure grading rules first before setting attendance policy', 'warning');
                  setAttendanceBlockedWarning(true);
                  setTimeout(() => setAttendanceBlockedWarning(false), 5000);
                  return;
                }
                setActiveCard(CARD_ATTENDANCE);
              }}
              className={`group relative bg-white p-8 rounded-3xl border transition-all duration-300 text-left overflow-hidden ${
                !hasGradingRules 
                  ? 'border-gray-200 opacity-60 cursor-not-allowed' 
                  : 'border-gray-200 shadow-sm hover:shadow-xl hover:border-emerald-300'
              }`}
            >
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Icons.Attendance />
              </div>
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform ${
                !hasGradingRules ? 'bg-gray-100 text-gray-400' : 'bg-emerald-50 text-emerald-600 group-hover:scale-110'
              }`}>
                <Icons.Attendance />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Attendance Policy</h3>
              <p className="text-gray-500 mb-6 leading-relaxed">
                Set attendance thresholds, blocking rules, and manage approval workflows.
              </p>
              <div className="flex flex-wrap gap-2">
                {!hasGradingRules ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-1 rounded flex items-center gap-1">
                    <Icons.Warning />
                    Requires Grading Rules First
                  </span>
                ) : (
                  <>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                      {hasAttendancePolicy?.enabled ? `${hasAttendancePolicy.defaultMinimumPercentage || 75}% Required` : 'Disabled'}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {hasAttendancePolicy?.strictEnforcement ? 'Strict Mode' : 'Flexible'}
                    </span>
                  </>
                )}
              </div>
              {!hasGradingRules && (
                <div className="absolute bottom-4 left-8 right-8 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                  ⚠️ Set up grading rules before configuring attendance
                </div>
              )}
            </button>
          </div>
        )}

        {/* Content Views */}
        {!showPresets && activeCard && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Contextual Navigation Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-4 rounded-2xl shadow-sm border border-gray-200 mb-8 gap-4">
              <button
                onClick={() => setActiveCard(null)}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 font-semibold transition-colors"
              >
                <Icons.Back />
                Return to Dashboard
              </button>
              
              <div className="flex items-center gap-3">
                <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest ${
                  activeCard === CARD_GRADING ? 'bg-blue-100 text-blue-700' : 
                  activeCard === CARD_ATTENDANCE ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'
                }`}>
                  {activeCard === CARD_GRADING ? 'Grading & Assessment' : 
                   activeCard === CARD_ATTENDANCE ? 'Attendance Management' : 'Template Library'}
                </span>
                
                {activeCard === CARD_GRADING && (
                  <button
                    onClick={() => setActiveCard(CARD_TEMPLATES)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-md transition-all text-sm font-bold"
                  >
                    <Icons.Template />
                    Use Template
                  </button>
                )}
              </div>
            </div>

            {/* Active Component Wrapper */}
            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
              <div className="p-1">
                {activeCard === CARD_GRADING && (
                  <div className="p-8">
                    <AcademicSystemConfig
                      rules={rules}
                      onUpdate={updateRules}
                      loading={saving}
                      disabled={!effectiveUserId}
                    />
                    <div className="mt-12 pt-8 border-t border-gray-100">
                      <RulesSummary rules={rules} />
                    </div>
                  </div>
                )}

                {activeCard === CARD_ATTENDANCE && (
                  <div className="p-8">
                    <AttendancePolicyConfig
                      policy={rules?.attendancePolicy}
                      onUpdate={updateAttendancePolicy}
                      loading={saving}
                      disabled={!effectiveUserId}
                      gradingRulesConfigured={hasGradingRules}
                    />
                  </div>
                )}

                {activeCard === CARD_TEMPLATES && (
                  <div className="p-8 bg-gray-50/30">
                    <div className="mb-8">
                      <h2 className="text-2xl font-bold text-gray-900">Preset Configurations</h2>
                      <p className="text-gray-500 mt-1">
                        Choose a template to instantly populate your academic rules based on global standards.
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Available: School (Percentage), University (Grade Based), Engineering (CGPA), Training (Pass/Fail), Medical College
                      </p>
                    </div>
                    <PresetTemplates
                      onApplyTemplate={applyTemplate}
                      loading={saving}
                      disabled={!effectiveUserId}
                    />
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={() => setActiveCard(CARD_GRADING)}
                        className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm"
                      >
                        Cancel & Continue Manual Setup →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Initial Empty State / Presets Only */}
        {showPresets && !activeCard && (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-50 text-indigo-600 rounded-3xl mb-4">
                <Icons.Template />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Welcome to Academic Configuration</h2>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                No grading rules found for this tenant. Select a template below to get started.
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Grading rules must be configured before setting up attendance policy
              </p>
            </div>
            <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm">
              <PresetTemplates
                onApplyTemplate={applyTemplate}
                loading={saving}
                disabled={!effectiveUserId}
              />
              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    setShowPresets(false);
                    setActiveCard(CARD_GRADING);
                  }}
                  className="text-sm text-gray-400 hover:text-gray-600"
                >
                  Skip template, configure manually →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Status */}
        {!showPresets && !activeCard && hasGradingRules && (
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-200">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-500">
                Active: {rules.gradingType?.toUpperCase()} Grading | 
                Pass: {rules.overallPassingPercentage}% | 
                {rules.requireBothToPass ? ' Must pass both components' : ' Aggregate passing only'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectAttendanceRules;